import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $nodesOfType } from 'lexical';
import { useEffect, useRef } from 'react';

import { $isImageNode, ImageNode } from './ImageNode';
import { useImageHandlers } from './useImageHandlers';

/**
 * Re-resolves images stored by `data-file-id` once on load, the way `FroalaInput` does with
 * `onResolveImage`: the saved markup keeps the id, and the host swaps in a fresh (often signed
 * and short-lived) URL. Runs once — images inserted later already carry a resolved URL.
 */
export const ImageResolverPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { resolve } = useImageHandlers();
  const ran = useRef(false);

  useEffect(() => {
    if (!resolve || ran.current) return;
    ran.current = true;

    const pending: { key: string; fileId: string }[] = [];
    editor.getEditorState().read(() => {
      for (const node of $nodesOfType(ImageNode)) {
        const fileId = node.getFileId();
        if (fileId) pending.push({ key: node.getKey(), fileId });
      }
    });

    for (const { key, fileId } of pending) {
      resolve(fileId)
        .then((url) => {
          if (!url) return;
          editor.update(() => {
            const node = $getNodeByKey(key);
            if ($isImageNode(node)) node.setSrc(url);
          });
        })
        .catch(() => undefined);
    }
  }, [editor, resolve]);

  return null;
};
